import { Info } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../../../../components/ui/common/button";
import { Form } from "../../../../../components/ui/common/form";
import { FormInput } from "../../../../../components/ui/Input/FormInput/FormInput";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../../../components/ui/Input/FormUnitInput/FormUnitInput";
import { PER_WEIGHT_UNITS_CHEMICAL } from "../../../../../config/optionsConstants";
import RecordFormShell from "../../shared/components/recordList/RecordFormShell";
import { formValuesToRecord, recordToFormValues } from "../utils/chemicalRecords.utils";
import { ChemicalRecord, ChemicalRecordFormValues, SelectOptionList, TransportRoute } from "../types/chemicalRecords.types";
import ChemicalRouteSection from "./ChemicalRouteSection";

interface ChemicalRecordFormProps {
  rec: ChemicalRecord;
  isDraft?: boolean;
  title?: string;
  onDelete?: () => void;
  chemFunctionOptions: SelectOptionList;
  transportOptions: SelectOptionList;
  transportLabelMap: Record<string, string>;
  onSave: (patch: Partial<ChemicalRecord>) => void;
  onCancel?: () => void;
  onAddRoute: (chemId: string, route: Pick<TransportRoute, "type" | "distance">) => void;
  onUpdateRoute: (chemId: string, routeId: string, patch: Partial<TransportRoute>) => void;
  onRemoveRoute: (chemId: string, routeId: string) => void;
}

/** Add/edit form for one chemical record — reused for both the inline row editor and the draft panel. */
const ChemicalRecordForm: React.FC<ChemicalRecordFormProps> = ({
  rec,
  isDraft = false,
  title,
  onDelete,
  chemFunctionOptions,
  transportOptions,
  transportLabelMap,
  onSave,
  onCancel,
  onAddRoute,
  onUpdateRoute,
  onRemoveRoute
}) => {
  const form = useForm<ChemicalRecordFormValues>({
    mode: "onChange",
    defaultValues: recordToFormValues(rec)
  });

  const hasTransportRoute = form.watch("hasTransportRoute");

  const handleSubmit = form.handleSubmit(data => onSave(formValuesToRecord(data)));

  return (
    <Form {...form}>
      <RecordFormShell
        title={title}
        onDelete={onDelete}
        isDraft={isDraft}
        onCancel={onCancel}
        submitDisabled={isDraft && !form.formState.isValid}
        onSubmit={handleSubmit}
      >
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-2">
          <FormInput
            control={form.control}
            name="name"
            label="Chemical Name"
            required
            placeholder="e.g., NaOH"
            errorMessage="Chemical name is required"
          />
          <FormSelect
            control={form.control}
            name="function"
            label="Chemical"
            required
            options={chemFunctionOptions}
            placeholder="Select"
            errorMessage="Chemical type is required"
          />
          <FormUnitInput
            control={form.control}
            name="amount"
            label="Amount"
            required
            placeholder="0"
            errorMessage="Amount is required"
            validate={(v: string) => Number(v) > 0 || "Amount must be greater than 0"}
            unitName="unit"
            unitOptions={PER_WEIGHT_UNITS_CHEMICAL}
          />
        </div>

        {isDraft && (
          <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-[10px] tw-text-muted-foreground tw-italic">
            <Info className="tw-h-3 tw-w-3 tw-text-primary" />
            Save this chemical first, then you can add transportation route details.
          </p>
        )}

        {!isDraft && (
          <>
            <div className="tw-flex tw-items-center tw-gap-2 tw-pt-1">
              <Button
                type="button"
                variant={hasTransportRoute ? "default" : "outline"}
                size="sm"
                className="tw-text-[10px] tw-h-6 tw-px-3"
                onClick={() => form.setValue("hasTransportRoute", true)}
              >
                Yes, add transport
              </Button>
              <Button
                type="button"
                variant={!hasTransportRoute ? "default" : "outline"}
                size="sm"
                className="tw-text-[10px] tw-h-6 tw-px-3"
                onClick={() => form.setValue("hasTransportRoute", false)}
              >
                No transport data
              </Button>
              <span className="tw-text-[10px] tw-text-muted-foreground">
                Do you know the transportation route?
              </span>
            </div>
            {hasTransportRoute && (
              <ChemicalRouteSection
                chemId={rec.id}
                routes={rec.routes}
                transportOptions={transportOptions}
                transportLabelMap={transportLabelMap}
                onAddRoute={onAddRoute}
                onUpdateRoute={onUpdateRoute}
                onRemoveRoute={onRemoveRoute}
              />
            )}
          </>
        )}
      </RecordFormShell>
    </Form>
  );
};

export default ChemicalRecordForm;
